import { useContext } from 'react'
import { generatePath } from 'react-router-dom'
import { Badge, CompanyTag } from '@/components/ui/badge'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { IBLandingPageKey } from 'api/ClientArea/LandingPageDto'
import { getBaseUrlName } from 'api/ClientArea/PromoMaterialsOptionsDto'
import { NameDto } from 'api/Common/Types'
import { Link } from 'components/Link'
import { useAppSelector } from 'store'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'

import { PermissionsEnum } from '../../../api/Identity/Types/PermissionsEnum'
import { AuthSessionContext } from '../../../utils/AuthContext'
import { hasPermission } from '../../../utils/permissionsValidator'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

const BaseUrlCell = (props: GridCellProps) => {
  const promoMaterialsOptions = useAppSelector((state) => state.clientArea.promoMaterialsOptions)
  const promoMaterialBaseUrl = props.dataItem.promoMaterialBaseUrl
  const baseUrlName = getBaseUrlName(promoMaterialsOptions.baseUrls)(promoMaterialBaseUrl)

  return <td className='columns__content'>{baseUrlName}</td>
}

const LandingPageName = (props: GridCellProps) => {
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  return (
    <>
      {hasPermission(permissions, PermissionsEnum.CanViewPromoMaterial) ? (
        <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
          <Link
            to={{
              pathname: generatePath(links.ibLanding.detailsUrl as string, {
                id: props.dataItem.id,
                slug: 'details',
              }),
            }}
          >
            {props.dataItem.name}
          </Link>
        </td>
      ) : (
        <td className='columns__content'>{props.dataItem.name}</td>
      )}
    </>
  )
}

export const ibLandingColumn: GridColumn[] = [
  {
    title: 'Landing Page Name',
    field: IBLandingPageKey.name,
    cell: LandingPageName,
    show: true,
    className: 'table-columns__content',
    filter: 'text',
    minResizableWidth: 120,
  },
  {
    title: 'Language',
    field: IBLandingPageKey.language,
    cell: (props: GridCellProps) => {
      const languages = props.dataItem.languages || []
      return (
        <td className='columns__content'>{languages.map((x: NameDto) => x.name).join(', ')}</td>
      )
    },
    show: true,
    className: 'table-columns__content',
    filter: 'text',
    minResizableWidth: 120,
  },
  {
    title: 'Base URL',
    field: IBLandingPageKey.baseURL,
    cell: BaseUrlCell,
    show: true,
    className: 'table-columns__content',
    filter: 'text',
    minResizableWidth: 120,
  },
  {
    title: 'Path',
    field: IBLandingPageKey.path,
    show: true,
    className: 'table-columns__content',
    filter: 'text',
    minResizableWidth: 120,
  },
  {
    title: 'Entity',
    field: IBLandingPageKey.entity,
    cell: (props: GridCellProps) => {
      const tickmillCompany = props.dataItem.tickmillCompany || []
      if (!tickmillCompany?.id) return <td />

      return (
        <td style={{ textAlign: 'center' }}>
          <CompanyTag id={tickmillCompany.id}>{tickmillCompany.name}</CompanyTag>
        </td>
      )
    },
    show: true,
    className: 'table-columns__content',
    filter: 'text',
    width: 100,
    minResizableWidth: 80,
  },
  {
    title: 'Active',
    field: IBLandingPageKey.active,
    cell: (props: GridCellProps) => {
      const enabled = props.dataItem.enabled || false
      return (
        <td width={80} style={{ textAlign: 'center' }}>
          <Badge variant={enabled ? 'green' : 'red'}>{enabled ? 'Yes' : 'No'}</Badge>{' '}
        </td>
      )
    },
    show: true,
    className: 'table-columns__content',
    filter: 'text',
    width: 100,
    minResizableWidth: 80,
  },
  {
    title: 'End Date',
    field: IBLandingPageKey.endDate,
    cell: (props: GridCellProps) => (
      <td className='table-columns__date'>
        {formatLocalDateUTC(props.dataItem.endDateTime, true)}
      </td>
    ),
    show: true,
    className: 'table-columns__date',
    filter: 'text',
    width: 125,
  },
  {
    title: 'Last Updated',
    field: IBLandingPageKey.updatedDate,
    cell: (props: GridCellProps) => (
      <td className='table-columns__date'>
        {formatLocalDateUTC(props.dataItem.updatedDate, true)}
      </td>
    ),
    show: true,
    className: 'table-columns__date',
    filter: 'text',
    width: 125,
  },
]
